import Button from '@material-ui/core/Button';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import Menu from '@material-ui/core/Menu';
import { onlyUnique } from '../../../../utils/helpers';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import { isFilterActive } from '../../../Devices/components/Filters/provider';
import {
  Badge,
  Grid,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { FunctionComponent, useState } from 'react';
import { MedisanteTargetSystem } from '../../types';
import { Filter } from '../../../Devices/types';
import { queryStringFromFilters } from '../../../Devices/components/Filters/queryString';
import { calculateFilters } from '../../../Devices/components/Filters/Filters';
import { useHistory } from 'react-router-dom';
import useStyles from '../../../Devices/components/Filters/styles';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { statusDescriptions } from '../../../Devices/components/Filters/constants';
import { InfoOutlined } from '@material-ui/icons';
import { useGetTargetSystemsQuery } from '../../../../store/api-slice';
import { useSnackbar } from 'notistack';

interface Props {
  readonly filters: Filter[];
}

const Filters: FunctionComponent<Props> = ({ filters }) => {
  const classes = useStyles();
  const history = useHistory();
  const { authUser } = useAuthUser();
  const config = useConfig();
  const { enqueueSnackbar } = useSnackbar();

  const [anchorEls, setAnchorEls] = useState<{
    [key: string]: HTMLElement;
  }>({});

  const { data: targetSystems = [], isLoading } = useGetTargetSystemsQuery({
    apiBaseURL: config.apiBaseURL,
    orgs: authUser.selectedOrgs,
    filters: [],
  });

  let targetSystemNotGoodStatusCodes: MedisanteTargetSystem[] = [];
  if (targetSystems) {
    targetSystemNotGoodStatusCodes = targetSystems.filter((ts) => {
      return ts.lastStatusCode && ts.lastStatusCode > 0;
    });
  }

  const handleFilterChange = (newFilters: Filter[]) => {
    const queryString = queryStringFromFilters(newFilters);
    history.push({ search: queryString });
  };

  // Count active filters for each category
  const statusFilterCount = filters.filter(
    (f) => f.category === 'last_status_code'
  ).length;

  const configStatusFilterCount = filters.filter(
    (f) =>
      f.category === 'blockedWebhook' ||
      f.category === 'configurationWarning' ||
      f.category === 'defaultWebhook'
  ).length;

  const onClickMenuItem = (
    category: string,
    value: string,
    dialogName: string
  ) => {
    const newFilters = calculateFilters(category, value, filters);

    handleFilterChange(newFilters);

    const { [dialogName]: _, ...rest } = anchorEls;
    setAnchorEls(rest);
  };

  return (
    <>
      <Toolbar
        className={classes.toolbar}
        style={{ paddingLeft: 0, paddingRight: 0 }}
      >
        <Grid container justifyContent="flex-start" direction="row">
          <Button
            disabled={
              !targetSystems ||
              isLoading ||
              (targetSystems && targetSystemNotGoodStatusCodes.length === 0)
            }
            size="small"
            className={classes.filter}
            aria-owns={anchorEls['ts-status'] ? 'ts-status' : undefined}
            data-testid="status-button"
            aria-haspopup="true"
            onClick={(e) => {
              setAnchorEls({
                ...anchorEls,
                'ts-status': e.currentTarget,
              });
            }}
          >
            <Badge
              badgeContent={statusFilterCount}
              color="secondary"
              invisible={statusFilterCount === 0}
            >
              <span className={classes.model}>
                <span className="label">Last status</span>
                <KeyboardArrowDownIcon />
              </span>
            </Badge>
          </Button>
          <Menu
            id="ts-status"
            classes={{ paper: classes.menu }}
            anchorEl={anchorEls['ts-status']}
            keepMounted
            open={Boolean(anchorEls['ts-status'])}
            onClose={() => {
              const { 'ts-status': _, ...rest } = anchorEls;
              setAnchorEls(rest);
            }}
          >
            {targetSystemNotGoodStatusCodes
              .filter(
                onlyUnique((f1, f2) => f1.lastStatusCode === f2.lastStatusCode)
              )
              .map((ts) => (
                <MenuItem
                  key={ts.lastStatusCode}
                  onClick={() =>
                    onClickMenuItem(
                      'last_status_code',
                      // @ts-ignore
                      ts.lastStatusCode.toString(),
                      'ts-status'
                    )
                  }
                >
                  <Checkbox
                    edge="start"
                    checked={isFilterActive(
                      {
                        category: 'last_status_code',
                        // @ts-ignore
                        value: ts.lastStatusCode.toString(),
                      },
                      filters
                    )}
                  />
                  <Typography variant="body2">
                    {ts.lastStatusCode}
                    {ts.lastStatusCode &&
                    statusDescriptions[ts.lastStatusCode] ? (
                      <Tooltip title={statusDescriptions[ts.lastStatusCode]}>
                        <IconButton>
                          <InfoOutlined />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Typography>
                </MenuItem>
              ))}
          </Menu>
          <Button
            disabled={!targetSystems || isLoading}
            size="small"
            className={classes.filter}
            aria-owns={
              anchorEls['ts-config-status'] ? 'ts-config-status' : undefined
            }
            data-testid="status-config-button"
            aria-haspopup="true"
            onClick={(e) => {
              setAnchorEls({
                ...anchorEls,
                'ts-config-status': e.currentTarget,
              });
            }}
          >
            <Badge
              badgeContent={configStatusFilterCount}
              color="secondary"
              invisible={configStatusFilterCount === 0}
            >
              <span className={classes.model}>
                <span className="label">Config status</span>
                <KeyboardArrowDownIcon />
              </span>
            </Badge>
          </Button>

          <Menu
            id="ts-config-status"
            classes={{ paper: classes.menu }}
            anchorEl={anchorEls['ts-config-status']}
            keepMounted
            open={Boolean(anchorEls['ts-config-status'])}
            onClose={() => {
              const { 'ts-config-status': _, ...rest } = anchorEls;
              setAnchorEls(rest);
            }}
          >
            <MenuItem
              key="blockedWebhook"
              onClick={() =>
                onClickMenuItem('blockedWebhook', 'true', 'ts-config-status')
              }
            >
              <Checkbox
                edge="start"
                checked={isFilterActive(
                  {
                    category: 'blockedWebhook',
                    value: 'true',
                  },
                  filters
                )}
              />
              <Typography variant="body2">Blocked Webhook</Typography>
            </MenuItem>

            <MenuItem
              key="configurationWarning"
              onClick={() => {
                onClickMenuItem(
                  'configurationWarning',
                  'true',
                  'ts-config-status'
                );
              }}
            >
              <Checkbox
                edge="start"
                checked={isFilterActive(
                  {
                    category: 'configurationWarning',
                    value: 'true',
                  },
                  filters
                )}
              />
              <Typography variant="body2">Configuration Warning</Typography>
            </MenuItem>
            <MenuItem
              key="defaultWebhook"
              onClick={() =>
                onClickMenuItem('defaultWebhook', 'true', 'ts-config-status')
              }
            >
              <Checkbox
                edge="start"
                checked={isFilterActive(
                  {
                    category: 'defaultWebhook',
                    value: 'true',
                  },
                  filters
                )}
              />
              <Typography variant="body2">Default Webhook</Typography>
            </MenuItem>
          </Menu>

          <Grid item style={{ marginLeft: 'auto' }}>
            <Button
              disabled={!targetSystems || isLoading}
              size="small"
              color="primary"
              aria-owns={anchorEls['ts-actions'] ? 'ts-actions' : undefined}
              data-testid="actions-button"
              aria-haspopup="true"
              onClick={(e) => {
                setAnchorEls({
                  ...anchorEls,
                  'ts-actions': e.currentTarget,
                });
              }}
            >
              Actions <KeyboardArrowDownIcon />
            </Button>
            <Menu
              classes={{ paper: classes.menu }}
              anchorEl={anchorEls['ts-actions']}
              keepMounted
              open={Boolean(anchorEls['ts-actions'])}
              onClose={() => {
                const { 'ts-actions': _, ...rest } = anchorEls;
                setAnchorEls(rest);
              }}
              id="ts-actions"
            >
              <MenuItem
                onClick={(e) => {
                  let data = '';
                  targetSystems.forEach((ts) => {
                    let txt = `ID: ${ts.id}
Name: ${ts.name}
URL: ${ts.url}
Format: ${ts.format}
Device count: ${ts.deviceCount || 0}
Owner: ${ts.owner}
Last status code: ${ts.lastStatusCode || 'none'}
Auth type: ${ts.authType}
Is default: ${ts.isDefault ? 'yes' : 'no'}
Request count: ${ts.count || 0}
Test request count: ${ts.testCount || 0}
Deactivated: ${ts.deactivated ? 'yes' : 'no'}\n\n`;
                    data += txt;
                  });

                  navigator.clipboard.writeText(data);
                  enqueueSnackbar(
                    `${
                      targetSystems.length || '0'
                    } webhooks copied to clipboard`,
                    { variant: 'info' }
                  );
                  const { 'ts-actions': _, ...rest } = anchorEls;
                  setAnchorEls(rest);
                }}
              >
                <Typography variant="body2">Copy to clipboard</Typography>
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>
      </Toolbar>
    </>
  );
};

export default Filters;
